import type { CSSProperties } from 'react';

import useMartyContext from 'hooks/useMartyContext';
import { ZAPPOS_MERCHANTID } from 'constants/crossSiteMerchantIdMapping';
import {
  LEGACY_SWATCH_PADDING,
  LEGACY_SWATCH_SIZE,
  LEGACY_SWATCH_SIZE_WITH_PADDING,
  REDESIGN_SWATCH_PADDING,
  REDESIGN_SWATCH_SIZE,
  REDESIGN_SWATCH_SIZE_WITH_PADDING
} from 'constants/colorSwatchMap';

interface SwatchSizing {
  isZappos: boolean;
  SWATCH_SIZE: number;
  SWATCH_PADDING: number;
  SWATCH_SIZE_WITH_PADDING: number;
  swatchContainerStyle: CSSProperties;
  swatchListStyle: CSSProperties;
}

/**
 * Resolves the PLP product-card swatch geometry per marketplace.
 *
 * The product-card redesign is permanent on Zappos (100% rolled out). 6pm keeps the legacy UI.
 * Returns both the raw dimensions (used for the visible-swatch count calculation)
 * and the ready-to-spread CSS-var payloads (applied to the swatch container and list):
 *   - Zappos: 26px swatch + 3px padding → 32px footprint.
 *   - 6pm:    34px swatch + 4px padding → 42px footprint.
 *
 * This hook is the single source of truth for swatch sizing. Consuming it in one place keeps the
 * parent component (ColorSwatches) and any future consumers that mirror the swatch footprint in
 * sync; add new size-related values here rather than branching on the marketplace at each call site.
 */
export const useSwatchSizing = (): SwatchSizing => {
  const {
    marketplace: { merchantId }
  } = useMartyContext();
  const isZappos = merchantId === ZAPPOS_MERCHANTID;

  const SWATCH_SIZE = isZappos ? REDESIGN_SWATCH_SIZE : LEGACY_SWATCH_SIZE;
  const SWATCH_PADDING = isZappos ? REDESIGN_SWATCH_PADDING : LEGACY_SWATCH_PADDING;
  const SWATCH_SIZE_WITH_PADDING = isZappos ? REDESIGN_SWATCH_SIZE_WITH_PADDING : LEGACY_SWATCH_SIZE_WITH_PADDING;

  // Static CSS-var payloads for the swatch container and list.
  const swatchContainerStyle = {
    '--color-swatch-container-height': `${SWATCH_SIZE_WITH_PADDING}px`
  } as CSSProperties;

  const swatchListStyle = {
    '--color-swatch-size': `${SWATCH_SIZE}px`,
    '--color-swatch-x-padding': `${SWATCH_PADDING}px`,
    '--color-swatch-y-padding': `${SWATCH_PADDING}px`
  } as CSSProperties;

  return {
    isZappos,
    SWATCH_SIZE,
    SWATCH_PADDING,
    SWATCH_SIZE_WITH_PADDING,
    swatchContainerStyle,
    swatchListStyle
  };
};
